import type { ReactNode, RefObject } from 'react';
import { Dialog } from '@paige/ui';
import { useEmailPreferences, useSetEmailPreferences } from '../../api/email-preference-queries';
import { EmailChoices } from './EmailChoices';
import { NoticeChoices } from './NoticeChoices';
import './second-factor.css';

export interface EmailPreferencesSheetProps {
  tenantId: string;
  onClose: () => void;
  /** What gets the keyboard back when the sheet closes: the profile's own button. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * "Emails and notices", from the profile menu: the person's lifecycle mail choices and, under them, which
 * of Paige's notices reach them in the bell and by email (NoticeChoices), in the shared drawer (it holds
 * the keyboard, closes on Escape and hands the focus back), the same one as two-step sign-in. Turning the tips on here opts in for this company, to the
 * person's own address. An email's "Choose which notices you get" link opens it (`#notices`).
 */
export function EmailPreferencesSheet({
  tenantId,
  onClose,
  returnFocusTo,
}: EmailPreferencesSheetProps): ReactNode {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      variant="drawer"
      size="sm"
      title="Emails and notices"
      {...(returnFocusTo ? { returnFocusTo } : {})}
    >
      <SheetBody tenantId={tenantId} />
    </Dialog>
  );
}

/** The mail switches and what went wrong if anything did, then the notices. */
function SheetBody({ tenantId }: { tenantId: string }): ReactNode {
  const choices = useEmailPreferences();
  const change = useSetEmailPreferences(tenantId);
  const reading = choices.isError
    ? 'Your email choices could not be read. Try again in a moment.'
    : 'Reading your email choices…';
  return (
    <>
      {choices.data ? (
        <EmailChoices
          choices={choices.data}
          busy={change.isPending}
          canStartTips
          onChange={(c) => change.mutate(c)}
        />
      ) : (
        <p className="sheet__line">{reading}</p>
      )}
      {change.isError && (
        <p className="sheet__line sheet__line--error" role="alert">
          That change did not save. Try again in a moment.
        </p>
      )}
      <NoticeChoices tenantId={tenantId} />
    </>
  );
}
